import { useCallback, useEffect } from 'react'

import { usePollingEffect } from '../../../apps/professional-app/fa/common/hooks/usePollingEffect'

import { DocumentSummaryExecutionResult } from '../../../models/ProcessDocument'
import { AIAgentType } from '../../../models/AIAgent'

import { selectDocumentBatchPollingEnabled, setDocumentBatchDocuments } from '../../../store/processDocument/documentBatch'
import {
  selectDocumentPollingEnabled,
  selectDocumentSummaryPollingEnabled,
  setDocument,
  setDocumentIsNotRelatedToClient,
  setDocumentSummaryAiAgentId,
  setDocumentSummaryResult,
  setDocumentSummarySyncStatus,
  setSelectedDocumentType,
} from '../../../store/processDocument/document'
import { useLazyGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import { useLazyGetDocumentByIdQuery, useLazyGetDocumentsByBatchQuery } from '../../../store/ai/documents'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { selectDocumentTypes } from '../../../store/processDocument/documentTypes'

export const usePollDocumentBatch = (documentBatchId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const pollingEnabled = useAppSelector(selectDocumentBatchPollingEnabled)

  const [triggerGetDocumentsByBatch] = useLazyGetDocumentsByBatchQuery()

  const getDocumentBatch = useCallback(async () => {
    const documents = await triggerGetDocumentsByBatch({ accountId, documentBatchId }).unwrap()
    if (!documents) {
      return
    }
    dispatch(setDocumentBatchDocuments(documents))
  }, [accountId, dispatch, documentBatchId, triggerGetDocumentsByBatch])

  const { start, stop } = usePollingEffect(getDocumentBatch, [], { interval: 5000, leading: false, stopAfterFailedAttempts: 5 })

  useEffect(() => {
    if (pollingEnabled) {
      start()
    } else {
      stop()
    }
  }, [pollingEnabled, start, stop])
}

export const usePollDocument = (documentId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const pollingEnabled = useAppSelector(selectDocumentPollingEnabled)
  const documentTypes = useAppSelector(selectDocumentTypes)

  const [triggerGetDocumentById] = useLazyGetDocumentByIdQuery()

  const getDocument = useCallback(async () => {
    if (!documentTypes.length) {
      return
    }

    const document = await triggerGetDocumentById({ accountId, documentId }).unwrap()
    if (!document) {
      return
    }

    dispatch(setDocument(document))

    const { typeId } = document.typeProcessing

    const documentType = documentTypes.find((documentType) => documentType.id === typeId)
    if (!documentType) {
      return
    }

    dispatch(setSelectedDocumentType(documentType))
  }, [accountId, dispatch, documentId, documentTypes, triggerGetDocumentById])

  const { start, stop } = usePollingEffect(getDocument, [], { interval: 5000, leading: false, stopAfterFailedAttempts: 5 })

  useEffect(() => {
    if (pollingEnabled) {
      start()
    } else {
      stop()
    }
  }, [pollingEnabled, start, stop])
}

export const usePollDocumentSummary = (documentId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const pollingEnabled = useAppSelector(selectDocumentSummaryPollingEnabled)

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()

  const getDocumentSummary = useCallback(async () => {
    const aiAgentResult = await triggerGetAiAgentResult({ accountId, type: AIAgentType.DOCUMENT_SUMMARY, resultKey: documentId }).unwrap()
    if (!aiAgentResult) {
      return
    }

    const { originalAiAgentId, result, interaction, syncStatus } = aiAgentResult
    const { aiAgentId } = interaction ?? {}

    const { summary, documentIsNotRelatedToClient } = result as DocumentSummaryExecutionResult

    dispatch(setDocumentSummaryAiAgentId(aiAgentId ?? originalAiAgentId))
    dispatch(setDocumentSummaryResult({ summary }))
    dispatch(setDocumentSummarySyncStatus(syncStatus))

    if (documentIsNotRelatedToClient) {
      dispatch(setDocumentIsNotRelatedToClient(true))
    }
  }, [accountId, dispatch, documentId, triggerGetAiAgentResult])

  const { start, stop } = usePollingEffect(getDocumentSummary, [], { interval: 5000, leading: false, stopAfterFailedAttempts: 5 })

  useEffect(() => {
    if (pollingEnabled) {
      start()
    } else {
      stop()
    }
  }, [pollingEnabled, start, stop])
}
